import React from "react";
import PropTypes from "prop-types";
import { createUseStyles, useTheme } from "react-jss";
import CountUp from "react-countup";

import locale from "../locale";
import { ResultComparisonUnit } from "../components/ResultComparisonUnit";
import ResultPerDeviceTable from "../components/ResultPerDeviceTable";
import {
  CO2_KG_PER_ALL_IN_ONE,
  CO2_KG_PER_DESKTOP,
  CO2_KG_PER_MONITOR,
  CO2_KG_PER_NOTEBOOK,
  CO2_KG_PER_PERIPHERAL_DEVICE,
  CO2_KG_PER_SMARTPHONE,
  CO2_KG_PER_TABLET,
  CONVERSION_ESPRESSO_PER_KG_CO2,
  CONVERSION_FLIGHT_KM_PER_KG_CO2,
  CONVERSION_VIDEOCONFERENCE_HOURS_PER_KG_CO2,
  COUNTER_DURATION_SEC,
} from "../calculationParams";

const useStyles = createUseStyles({
  calculatorResults: {
    minHeight: 253,
    display: "flex",
    flexDirection: "column",
  },
  resultMainSection: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "stretch",
    marginTop: 48,
    borderTop: ({ theme }) => `1px solid ${theme.dustinBorderDivider}`,
  },
  resultMainTopContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  resultMainDeviceCategoriesContainer: {
    marginTop: 96,
    marginBottom: 48,
  },
  resultCopy: {
    display: "block",
    marginBlockStart: "1em",
    marginBlockEnd: "1em",
    marginInlineStart: 0,
    marginInlineEnd: 0,
    fontSize: 20,
    lineHeight: "24px",
    fontWeight: 400,
    color: "white",
  },
  resultMainAmount: {
    fontSize: 120,
    margin: 0,
    color: ({ theme }) => theme.dustinGreen,
    "@media (max-width: 991px)": {
      fontSize: 48,
      lineHeight: "57.6px",
    },
  },
  resultCompareUnitsSection: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "space-between",
    marginTop: 48,
    borderTop: ({ theme }) => `1px solid ${theme.dustinBorderDivider}`,
  },
  resultComparisonUnitsContainer: {
    marginTop: 24,
    marginBottom: 24,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  resultTreeSection: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginTop: 48,
    borderTop: ({ theme }) => `1px solid ${theme.dustinBorderDivider}`,
    flexWrap: "wrap",
  },
  resultTreeAmount: {
    fontSize: 96,
    marginTop: 0,
    color: ({ theme }) => theme.dustinGreen,
    "@media (max-width: 991px)": {
      fontSize: 48,
      lineHeight: "57.6px",
    },
  },
});

const CalculatorResults = ({
  amountNotebook,
  amountPeripheralDevice,
  amountSmartphone,
  amountTablet,
  amountDesktop,
  amountAllInOne,
  amountMonitor,
}) => {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const LL = locale.results;

  const amountKgCO2Total =
    amountNotebook * CO2_KG_PER_NOTEBOOK +
    amountPeripheralDevice * CO2_KG_PER_PERIPHERAL_DEVICE +
    amountSmartphone * CO2_KG_PER_SMARTPHONE +
    amountTablet * CO2_KG_PER_TABLET +
    amountDesktop * CO2_KG_PER_DESKTOP +
    amountAllInOne * CO2_KG_PER_ALL_IN_ONE +
    amountMonitor * CO2_KG_PER_MONITOR;

  const amountFlightKm = amountKgCO2Total * CONVERSION_FLIGHT_KM_PER_KG_CO2;

  const amountVideoconferencingHours =
    amountKgCO2Total * CONVERSION_VIDEOCONFERENCE_HOURS_PER_KG_CO2;

  const amountEspresso =
    amountKgCO2Total * CONVERSION_ESPRESSO_PER_KG_CO2;

  return (
    <div className={classes.calculatorResults}>
      <div className={classes.resultMainSection}>
        <div className={classes.resultMainTopContainer}>
          <div className={classes.resultCopy}>{LL.headerCopy}</div>
          {amountKgCO2Total > 1000 ? (
            <div className={classes.resultMainAmount}>
              <CountUp
                duration={COUNTER_DURATION_SEC}
                end={amountKgCO2Total / 1000.0}
                separator={" "}
                decimals={2}
              />
              {` ${LL.unitTonnes}`}
            </div>
          ) : (
            <div className={classes.resultMainAmount}>
              <CountUp
                duration={COUNTER_DURATION_SEC}
                end={amountKgCO2Total}
                separator={" "}
              />
              {` ${LL.unitKg}`}
            </div>
          )}
        </div>
        {amountKgCO2Total > 0 && (
          <div className={classes.resultMainDeviceCategoriesContainer}>
            <ResultPerDeviceTable
              amountNotebook={amountNotebook}
              amountPeripheralDevice={amountPeripheralDevice}
              amountSmartphone={amountSmartphone}
              amountTablet={amountTablet}
              amountDesktop={amountDesktop}
              amountAllInOne={amountAllInOne}
              amountMonitor={amountMonitor}
            />
          </div>
        )}
      </div>
      {amountKgCO2Total > 0 && (
        <div>
          <div className={classes.resultCompareUnitsSection}>
            <div className={classes.resultCopy}>{LL.comparisons.headerCopy}</div>
            <div className={classes.resultComparisonUnitsContainer}>
              <ResultComparisonUnit
                topCopy={LL.comparisons.flightKmTop}
                amount={amountFlightKm}
                bottomCopy={LL.comparisons.flightKmBottom(amountFlightKm)}
              />
              <ResultComparisonUnit
                topCopy={LL.comparisons.videoConferenceTop}
                amount={amountVideoconferencingHours}
                bottomCopy={LL.comparisons.videoConferenceBottom(
                  amountVideoconferencingHours
                )}
              />
              <ResultComparisonUnit
                topCopy={LL.comparisons.espressosTop}
                amount={amountEspresso}
                bottomCopy={LL.comparisons.espressosBottom(amountEspresso)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

CalculatorResults.propTypes = {
  amountNotebook: PropTypes.number.isRequired,
  amountPeripheralDevice: PropTypes.number.isRequired,
  amountSmartphone: PropTypes.number.isRequired,
  amountTablet: PropTypes.number.isRequired,
  amountDesktop: PropTypes.number.isRequired,
  amountAllInOne: PropTypes.number.isRequired,
  amountMonitor: PropTypes.number.isRequired,
};

export default CalculatorResults;



// WEBPACK FOOTER //
// ./src/app/CalculatorResults.js